import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import Button from 'modules/_shared/components/Buttons';
import { TradingOffer } from '../../../../../types/TradingOffer';

type TradingOfferActionCardProps = {
  offer: TradingOffer;
  availableShares: number;
  purchaseQuantity: number;
  commissionRate: number;
  isExpired: boolean;
  hasPartnerBuyer: boolean;
  onQuantityChange: (quantity: number) => void;
  onBuyShares: VoidFunction;
  onViewOtherOffers: VoidFunction;
};

const TradingOfferActionCard: FC<TradingOfferActionCardProps> = ({
  offer,
  availableShares,
  purchaseQuantity,
  commissionRate,
  isExpired,
  hasPartnerBuyer,
  onQuantityChange,
  onBuyShares,
  onViewOtherOffers,
}) => {
  const { t } = useTranslate();

  const isSellOffer = offer.type === 'SELL';

  const subtotal = useMemo(
    () => purchaseQuantity * offer.sharePrice,
    [purchaseQuantity, offer.sharePrice]
  );

  const total = useMemo(() => subtotal, [subtotal]);

  const quickAmounts = useMemo(() => {
    const amounts: number[] = [];
    if (availableShares >= 10) amounts.push(10);
    if (availableShares >= 25) amounts.push(25);
    if (availableShares > 0) amounts.push(availableShares);
    return amounts;
  }, [availableShares]);

  const handleIncrement = () => {
    if (purchaseQuantity < availableShares) {
      onQuantityChange(purchaseQuantity + 1);
    }
  };

  const handleDecrement = () => {
    if (purchaseQuantity > 1) {
      onQuantityChange(purchaseQuantity - 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value)) {
      onQuantityChange(value);
    }
  };

  if (isExpired) {
    return (
      <div
        className="card card-bordered"
        style={{ position: 'sticky', top: '20px' }}
      >
        <div className="card-inner text-center py-4">
          <p className="text-soft mb-3">{t('ExpiredOfferActionDescription')}</p>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onViewOtherOffers}
          >
            {t('ViewOtherOffers')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="card card-bordered"
      style={{ position: 'sticky', top: '20px' }}
    >
      <div className="card-inner">
        <div className="text-center mb-4">
          <em className="icon ni ni-cart fs-24px text-success" />
          <h5 className="mt-2 mb-1">
            {isSellOffer ? t('BuyShares') : t('SellShares')}
          </h5>
          <p className="text-soft fs-13px mb-0">
            {t('AcceptOfferDescription')}
          </p>
        </div>

        <hr />

        <div className="form-group mb-3">
          <label className="form-label" htmlFor="purchase-quantity">
            {t('HowManyShares')}
          </label>

          <div className="d-flex w-100 mb-2" style={{ gap: '6px' }}>
            {quickAmounts.map((amount) => (
              <Button
                key={amount}
                variant={purchaseQuantity === amount ? 'primary' : 'light'}
                onClick={() => onQuantityChange(amount)}
                dimStyle={!!(purchaseQuantity === amount)}
                className="flex-fill"
              >
                <span className="text-center">
                  {amount === availableShares
                    ? `${t('All')} (${formatNumber(amount)})`
                    : formatNumber(amount)}
                </span>
              </Button>
            ))}
          </div>

          <div className="input-group">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleDecrement}
              disabled={purchaseQuantity <= 1}
            >
              −
            </button>
            <input
              id="purchase-quantity"
              type="number"
              className="form-control text-center"
              value={purchaseQuantity}
              onChange={handleInputChange}
              min={1}
              max={availableShares}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleIncrement}
              disabled={purchaseQuantity >= availableShares}
            >
              +
            </button>
          </div>

          <span className="form-note mt-1">
            {t('MaxAvailable')}: {formatNumber(availableShares)}{' '}
            {t('Shares').toLowerCase()}
          </span>
        </div>

        <hr />

        <div className="mb-4">
          <h6 className="overline-title text-soft mb-3">
            {t('PurchaseSummary')}
          </h6>

          <div className="d-flex justify-content-between py-2">
            <span className="text-soft">{t('Shares')}</span>
            <strong>{formatNumber(purchaseQuantity)}</strong>
          </div>
          <div className="d-flex justify-content-between py-2">
            <span className="text-soft">{t('PricePerShare')}</span>
            <strong>{formatCurrency(offer.sharePrice)}</strong>
          </div>
          <div className="d-flex justify-content-between py-2 border-top">
            <span className="text-soft">{t('Subtotal')}</span>
            <strong>{formatCurrency(subtotal)}</strong>
          </div>
          <div className="d-flex justify-content-between py-3">
            <span className="fs-16px">{t('TotalToPay')}</span>
            <strong className="fs-20px text-success">
              {formatCurrency(total)}
            </strong>
          </div>
        </div>

        <div className="alert alert-light mb-4">
          <h6 className="alert-heading fs-13px">{t('AfterPurchase')}:</h6>
          <p className="mb-0 fs-13px">
            +{formatNumber(purchaseQuantity)} {t('Shares').toLowerCase()}
          </p>
        </div>

        <button
          type="button"
          className="btn btn-lg btn-success btn-block mb-3"
          onClick={onBuyShares}
          disabled={!hasPartnerBuyer}
        >
          <em className="icon ni ni-check-circle mr-2" />
          {isSellOffer
            ? `${t('Buy')} ${formatNumber(purchaseQuantity)} ${t(
                'Shares'
              ).toLowerCase()}`
            : `${t('Sell')} ${formatNumber(purchaseQuantity)} ${t(
                'Shares'
              ).toLowerCase()}`}
        </button>

        <div className="text-center">
          <button
            type="button"
            className="btn btn-link text-soft fs-13px"
            onClick={onViewOtherOffers}
          >
            <em className="icon ni ni-arrow-left mr-1" />
            {t('ViewOtherOffers')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradingOfferActionCard;
